import { Box, Button, HStack, Image, Text } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
// Vendored signature pad, already bundled for the AngularJS side. Cross-tree
// imports from public/ are established (see PatientTaskMetricsTable); the types
// come from the hand written signature_pad.d.ts beside it.
import type { SignaturePad as SignaturePadInstance } from "../../../../../public/vendors/signature_pad/signature_pad";
import { SignaturePad } from "../../../../../public/vendors/signature_pad/signature_pad";

const CANVAS_WIDTH = 520;
const CANVAS_HEIGHT = 140;

interface Props {
  /** Existing signature, as a data URL. */
  value: string | null;
  isDisabled: boolean;
  onChange: (dataUrl: string | null) => void;
}

export default function SignatureCanvas({ value, isDisabled, onChange }: Props) {
  // Drawing and displaying are separate components so the pad's effect never
  // runs conditionally.
  if (isDisabled) {
    return <StoredSignature value={value} />;
  }

  return <LiveSignaturePad value={value} onChange={onChange} />;
}

function LiveSignaturePad(props: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
}) {
  const { value, onChange } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const padRef = useRef<SignaturePadInstance | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return;
    }

    const pad: SignaturePadInstance = new SignaturePad(canvas, {
      onEnd: () => onChange(pad.toDataURL()),
    });
    padRef.current = pad;

    // Hydrate once on mount; later value changes originate from this pad, so
    // redrawing them would fight strokes in progress.
    if (value !== null) {
      pad.fromDataURL(value);
    }

    return () => {
      pad.off();
      padRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    padRef.current?.clear();
    onChange(null);
  };

  return (
    <HStack align="flex-end" spacing={3}>
      <Box borderRadius="md" borderWidth="1px" width="fit-content">
        <canvas
          ref={canvasRef}
          height={CANVAS_HEIGHT}
          style={{ cursor: "crosshair", display: "block", touchAction: "none" }}
          width={CANVAS_WIDTH}
        />
      </Box>
      <Button size="sm" variant="ghost" onClick={handleClear}>
        Clear
      </Button>
    </HStack>
  );
}

function StoredSignature(props: { value: string | null }) {
  if (props.value === null) {
    return (
      <Box borderRadius="md" borderWidth="1px" color="gray.500" padding={4}>
        <Text fontSize="sm">Not signed</Text>
      </Box>
    );
  }

  return (
    <Box borderRadius="md" borderWidth="1px" padding={2} width="fit-content">
      <Image alt="Signature" maxHeight={`${CANVAS_HEIGHT}px`} src={props.value} />
    </Box>
  );
}
